import { type FC, useCallback, useState } from 'react';

import Banner from '@/components/UI/Banner';
import { validateClashWindow } from '@/components/UI/DataTableV2/DataTable.util';
import { TimepointInput } from '@/components/UI/Timepoint/TimepointInput';
import { _isNotBlank } from '@/littledash';
import type { Nullable } from '@/model/Common.model';
import { ModalActions, ModalContainer, ModalHeader } from '@/utils/modal';

export interface ClashWindowModalProps {
  /** Current window as an ISO-8601 duration, or `null` when clash alerts are off. */
  clashWindow: Nullable<string>;
  onSave: (clashWindow: Nullable<string>) => void;
  closeModal: () => void;
}

/**
 * Sets the dosing plan's clash window — how close two dose timestamps have to be before the sheet
 * warns about them. Clearing the input turns the warnings off rather than setting a zero window.
 */
const ClashWindowModal: FC<ClashWindowModalProps> = ({ clashWindow, onSave, closeModal }) => {
  const [value, setValue] = useState<Nullable<string>>(clashWindow);
  const validation = validateClashWindow(value ?? null);
  const error = validation === true ? undefined : validation;

  const handleSave = useCallback(() => {
    if (validation !== true) {
      return;
    }
    onSave(_isNotBlank(value) ? value : null);
    closeModal();
  }, [validation, value, onSave, closeModal]);

  return (
    <ModalContainer size="narrow" className="bg-white" testKey="ClashWindowModal">
      <ModalHeader title="Clash window" closeModal={closeModal} className="pa3 bb b--moon-gray w-100" />

      <div className="pa3" data-testid="clash-window__body">
        <label className="db" htmlFor="clash-window-input">
          Warn me when timestamps are within:
        </label>
        <div className="mt2" data-testid="clash-window__input">
          <TimepointInput
            id="clash-window-input"
            value={value}
            defaultUnit="minutes"
            invalid={_isNotBlank(error)}
            onChange={({ value: next }) => setValue(_isNotBlank(next) ? next : null)}
          />
        </div>
        {_isNotBlank(error) && (
          <p className="f6 red db pv1" data-testid="clash-window__error">
            {error}
          </p>
        )}

        <Banner info dismiss={false} className="mt3" testId="clash-window__hint">
          <p className="f6 lh-copy">Clash Window Alerts are turned off when no time is provided.</p>
        </Banner>
      </div>

      <ModalActions
        className="pa3 bt b--moon-gray w-100"
        onSubmit={handleSave}
        onCancel={closeModal}
        submitBtnText="Save"
        cancelBtnText="Cancel"
        submitButtonProps={{ disabled: _isNotBlank(error), testId: 'clash-window__submit' }}
        cancelButtonProps={{ testId: 'clash-window__cancel' }}
      />
    </ModalContainer>
  );
};

export default ClashWindowModal;
